import { useGraphQLMutation } from '@opengov/bnp-core';
import { useQueryClient } from '@tanstack/react-query';
import { IPublicationFooterSettings } from '@opengov/stories-shared/types';

import { graphql } from '../../../../../gql-generated/hasura';
import {
  UpdateFooterSettingsForOnlinePublicationMutation,
  UpdateFooterSettingsForOnlinePublicationMutationVariables
} from '../../../../../gql-generated/hasura/graphql';
import { HASURA_SERVICE_URL } from '../../../../../config/config';
import { useToast } from '../../../Toast/context/useToast.context';

import { setupOnlinePublicationQueryKeys } from './setupOnlinePublicationQueryKeys';

const UpdateFooterSettingsForOnlinePublicationDocument = graphql(`
  mutation UpdateFooterSettingsForOnlinePublication(
    $id: uuid!
    $set: publicationFooterSettings_set_input!
  ) {
    update_publicationFooterSettings_by_pk(pk_columns: { id: $id }, _set: $set) {
      id
      publicationId
      backgroundColors
      fontColors
      mediaLinks
    }
  }
`);

export type UseSaveFooterSettingsMessages = {
  errorTitle: string;
  errorBody?: string;
};

export type UseSaveFooterSettingsContext = {
  entityId: number;
  publicationId: string;
};

export function useSaveFooterSettings(
  context: UseSaveFooterSettingsContext,
  messages: UseSaveFooterSettingsMessages
) {
  const queryClient = useQueryClient();
  const { toggleToast } = useToast();

  return useGraphQLMutation<
    UpdateFooterSettingsForOnlinePublicationMutation,
    UpdateFooterSettingsForOnlinePublicationMutationVariables
  >({
    mutationKey: [...setupOnlinePublicationQueryKeys.all, 'saveFooterSettings', context],
    document: UpdateFooterSettingsForOnlinePublicationDocument,
    url: HASURA_SERVICE_URL,
    options: {
      onSuccess: (data) => {
        const updated = data?.update_publicationFooterSettings_by_pk;
        if (updated) {
          queryClient.setQueryData(
            setupOnlinePublicationQueryKeys.publicationFooterSettings({
              entityId: context.entityId,
              publicationId: context.publicationId
            }),
            (old: { publicationFooterSettings: IPublicationFooterSettings[] } | undefined) => {
              const [first, ...rest] = old?.publicationFooterSettings ?? [];
              return {
                publicationFooterSettings: [
                  { ...(first ?? {}), ...(updated as unknown as IPublicationFooterSettings) },
                  ...rest
                ]
              };
            }
          );
        }
      },
      onError: () =>
        toggleToast({
          title: messages.errorTitle,
          body: messages.errorBody,
          severity: 'error'
        })
    }
  });
}
